import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Building2,
  CircleDashed,
  GitBranch,
  Loader2,
  RadioTower,
  ServerCog,
  ShieldCheck,
  Timer,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useUser } from '@/hooks/useUser'
import {
  useBosunKanbanSummary,
  useBosunProjects,
  useWorkspaces,
  type BosunKanbanSummaryRead,
  type BosunProjectRead,
  type WorkspaceRead,
} from '@/api/queries'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { ProjectReleaseNotesPanel } from '@/components/ProjectReleaseNotesPanel'
import { formatError } from '@/lib/format-error'
import { canManageMembers } from '@/api/workspace-permissions'

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
})

const ROLE_TONE: Record<string, React.ComponentProps<typeof Badge>['tone']> = {
  owner: 'primary',
  admin: 'warning',
  editor: 'success',
  viewer: 'neutral',
}

const KANBAN_TONES: Record<string, React.ComponentProps<typeof Badge>['tone']> = {
  blocked: 'danger',
  in_progress: 'info',
  waiting: 'warning',
  todo: 'neutral',
  done: 'success',
  canceled: 'neutral',
}

function KanbanTaskRow({
  workspaceUuid,
  projectUuid,
  task,
}: {
  workspaceUuid: string
  projectUuid: string
  task: BosunKanbanSummaryRead['blocked_tasks'][number]
}) {
  return (
    <Link
      to="/projects/$workspaceUuid/$projectUuid/provisioning"
      params={{ workspaceUuid, projectUuid }}
      aria-label={`Open ${task.title}`}
      className="block rounded-md border border-white/10 bg-white/[0.025] px-3 py-2 transition hover:border-primary/40 hover:bg-white/[0.05]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{task.title}</p>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {task.profile ?? task.assignee ?? task.owner ?? 'unassigned'}
            {(task.child_tasks?.length ?? 0) > 0
              ? ` · ${task.child_tasks?.length ?? 0} child task`
              : ''}
          </p>
        </div>
        <Badge tone={KANBAN_TONES[task.status] ?? 'neutral'} size="xs">
          {task.status.replace('_', ' ')}
        </Badge>
      </div>
    </Link>
  )
}

export function ProjectKanbanPanel({
  workspaceUuid,
  project,
}: {
  workspaceUuid: string
  project: BosunProjectRead
}) {
  const { data: summary, isLoading, error } = useBosunKanbanSummary(workspaceUuid, project.uuid)

  if (isLoading) {
    return <Skeleton className="h-28 w-full" />
  }
  if (error) {
    return (
      <div className="rounded-md border border-dashed border-white/10 p-3 text-sm text-muted-foreground">
        No persisted Hermes board snapshot is available for {project.name}.
      </div>
    )
  }
  if (!summary) return null

  const highlightedTasks = [
    ...summary.blocked_tasks,
    ...summary.running_tasks,
    ...summary.waiting_tasks,
  ].slice(0, 5)

  return (
    <div className="space-y-3 rounded-lg border border-white/10 bg-black/15 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{project.name}</p>
          <p className="truncate text-xs text-muted-foreground">{summary.board_slug}</p>
        </div>
        <Badge tone={summary.counts.blocked > 0 ? 'danger' : 'success'} size="sm">
          {summary.counts.total} tasks
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <KanbanCount icon={AlertTriangle} label="Blocked" value={summary.counts.blocked} tone="danger" />
        <KanbanCount icon={Loader2} label="Running" value={summary.counts.in_progress} tone="info" />
        <KanbanCount icon={CircleDashed} label="Waiting" value={summary.counts.waiting} tone="warning" />
      </div>
      {highlightedTasks.length > 0 ? (
        <div className="space-y-2">
          {highlightedTasks.map((task) => (
            <KanbanTaskRow
              key={task.id}
              workspaceUuid={workspaceUuid}
              projectUuid={project.uuid}
              task={task}
            />
          ))}
        </div>
      ) : null}
      {summary.recent_release_train_events.length > 0 ? (
        <div className="space-y-2 border-t border-white/10 pt-3">
          {summary.recent_release_train_events.slice(0, 3).map((event) => (
            <Link
              key={event.id}
              to="/projects/$workspaceUuid/$projectUuid/provisioning"
              params={{ workspaceUuid, projectUuid: project.uuid }}
              className="flex items-start gap-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <GitBranch className="mt-0.5 size-3.5 shrink-0 text-primary" />
              <span className="min-w-0 truncate">
                {event.summary || event.kind}
                {event.profile ? ` · ${event.profile}` : ''}
              </span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function KanbanCount({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: LucideIcon
  label: string
  value: number
  tone: React.ComponentProps<typeof Badge>['tone']
}) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.035] p-2">
      <div className="mb-1 flex items-center justify-between gap-1">
        <Icon className="size-3.5 text-muted-foreground" />
        <Badge tone={tone} size="xs">
          {value}
        </Badge>
      </div>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  )
}

function WorkspaceOverview({ workspace }: { workspace: WorkspaceRead }) {
  const { data: projects, isLoading, error } = useBosunProjects(workspace.uuid)
  const canProvision = canManageMembers(workspace.role)
  const activeProjects = (projects ?? []).filter((project) => project.archive_status === 'active')

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="truncate">{workspace.name}</CardTitle>
            <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="size-3.5" />
              {workspace.member_count} member{workspace.member_count === 1 ? '' : 's'}
            </p>
          </div>
          <Badge tone={ROLE_TONE[workspace.role] ?? 'neutral'}>{workspace.role}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : error ? (
          <Alert tone="danger">
            <AlertDescription>{formatError(error)}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3">
                <p className="text-2xl font-semibold">{activeProjects.length}</p>
                <p className="text-xs text-muted-foreground">Active projects</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3">
                <p className="text-2xl font-semibold">
                  {activeProjects.filter((project) => project.status === 'ready').length}
                </p>
                <p className="text-xs text-muted-foreground">Ready</p>
              </div>
            </div>
            {activeProjects.slice(0, 2).map((project) => (
              <ProjectReleaseNotesPanel
                key={project.uuid}
                workspaceUuid={workspace.uuid}
                project={project}
              />
            ))}
          </div>
        )}
        {activeProjects.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Timer className="size-4 text-primary" />
              Hermes kanban visibility
            </div>
            {activeProjects.slice(0, 3).map((project) => (
              <ProjectKanbanPanel
                key={project.uuid}
                workspaceUuid={workspace.uuid}
                project={project}
              />
            ))}
          </div>
        ) : null}
        <Button
          asChild
          variant={canProvision && workspace.archive_status !== 'archived' ? 'default' : 'outline'}
          className="w-full"
        >
          {canProvision && workspace.archive_status !== 'archived' ? (
            <Link to="/projects/$workspaceUuid/new" params={{ workspaceUuid: workspace.uuid }}>
              Provision project
              <ArrowRight />
            </Link>
          ) : (
            <Link to="/projects">
              View projects
              <ArrowRight />
            </Link>
          )}
        </Button>
      </CardContent>
    </Card>
  )
}

export function DashboardPage() {
  const { user } = useUser()
  const { data: workspaces, isLoading, error } = useWorkspaces()
  const firstName = user?.name?.split(' ')[0]
  const firstProvisionableWorkspace = workspaces?.find(
    (workspace) => canManageMembers(workspace.role) && workspace.archive_status !== 'archived',
  )

  return (
    <div className="mx-auto w-full max-w-7xl space-y-8 px-4 py-6 md:px-8 md:py-8">
      <section className="relative overflow-hidden rounded-lg border border-white/10 bg-white/[0.035] p-5 shadow-floating backdrop-blur-xl md:p-7">
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"
        />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <PageHeader
            eyebrow="Bosun operations cockpit"
            title={firstName ? `Command ready, ${firstName}` : 'Command ready'}
            description="Provision customer stacks, watch workspace ownership, and keep Hermes-backed delivery systems moving from one control surface."
            actions={
              <Button asChild>
                {firstProvisionableWorkspace ? (
                  <Link
                    to="/projects/$workspaceUuid/new"
                    params={{ workspaceUuid: firstProvisionableWorkspace.uuid }}
                  >
                    New project
                    <ArrowRight />
                  </Link>
                ) : (
                  <Link to="/projects">
                    New project
                    <ArrowRight />
                  </Link>
                )}
              </Button>
            }
          />
          <div className="grid grid-cols-3 gap-2 rounded-lg border border-white/10 bg-black/20 p-2">
            <SignalTile icon={RadioTower} label="Gateway" value="Online" />
            <SignalTile icon={ShieldCheck} label="Secrets" value="Write-only" />
            <SignalTile icon={Activity} label="Audit" value="Live" />
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-3 p-5">
            <ServerCog className="size-5 text-primary" />
            <div>
              <p className="text-sm font-medium">Provisioning spine</p>
              <p className="text-xs text-muted-foreground">Repo, board, Hermes, infra</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-5">
            <ShieldCheck className="size-5 text-success" />
            <div>
              <p className="text-sm font-medium">Secret boundary</p>
              <p className="text-xs text-muted-foreground">Write-only credentials</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-5">
            <Building2 className="size-5 text-info" />
            <div>
              <p className="text-sm font-medium">Workspaces</p>
              <p className="text-xs text-muted-foreground">
                {workspaces?.length ?? 0} operations cells
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="space-y-3">
        <SectionHeader title="Workspaces" description="Every project is provisioned from an admin workspace." />
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : error ? (
          <Alert tone="danger">
            <AlertDescription>Failed to load workspaces: {formatError(error)}</AlertDescription>
          </Alert>
        ) : workspaces && workspaces.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {workspaces.map((workspace) => (
              <WorkspaceOverview key={workspace.uuid} workspace={workspace} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Building2}
            title="No workspaces yet"
            description="Create a workspace before provisioning a Bosun project."
            action={
              <Button asChild size="sm">
                <Link to="/workspaces">Open workspaces</Link>
              </Button>
            }
          />
        )}
      </section>
    </div>
  )
}

function SignalTile({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon
  label: string
  value: string
}) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.035] p-3">
      <Icon className="mb-3 size-4 text-primary" />
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xs font-semibold text-foreground">{value}</p>
    </div>
  )
}
